import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import ReconnectingWebSocket from 'reconnecting-websocket'

import { gesturesData } from 'components/Tiles/Student/Gestures'
import {
    VIBE,
    CHAT,
    TOMATO,
    CUPCAKE,
    NOTE_SAVE,
    NOTE_DELETE,
    ELSEWHERE,
    MUTE,
    WHITEBOARD_URL,
    QUIZ,
} from 'constants/socket'
import { getEnvUrls } from 'helpers/envUrls'
import {
    emitGestureMsg,
    emitShowReactionMsg,
    emitShowGiphyMsg,
    emitRevertGestureMsg,
    emitVibe,
    emitGreetingCupcake,
    emitGreetingTomato,
    emitElsewhereMessage,
    elsewhereKeys,
} from 'helpers/events'
import { generateUUID } from 'helpers/miscellaneous'
import useHeartbeat from 'hooks/useHeartbeat'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { GESTURE, REVERT_GESTURE } from 'services/webSockets/gestures/constants'
import { REACTION, EMOJI, GIPHY } from 'services/webSockets/reactions/constants'
import { ROOM_STATE } from 'services/webSockets/roomState/constants'
import { roomStateHandler } from 'services/webSockets/roomState/handler'
import { ACTIVE_SPEAKER_DATA } from 'services/webSockets/talktimeMonitor/constants'
import {
    dispatchReceiveChatMessage,
    dispatchReceiveTtmMessage,
    dispatchReceiveRoomStateMessage,
    dispatchReceiveAllMessage,
    dispatchSetSocket,
    dispatchReceiveAudioMessage,
    dispatchReceiveNoteMessage,
    dispatchDeleteNoteMessage,
    dispatchPopulateDummyNotesMessages,
    dispatchSetSenderIdForDummyNotes,
    dispatchUpdateParticipantInfo,
} from 'store/dispatcher'
import { _isInteractionMode } from 'store/selectors/Room'
import { _socket } from 'store/selectors/WebSocket'
import { isDesktop } from 'utils/device'
import { logger } from 'utils/logger'

export const isMessage = m => {
    let message = JSON.parse(m.data)

    if (message.action === CHAT) {
        dispatchReceiveChatMessage({
            ...message,
            chat_id: generateUUID(),
        })
    } else if (message.action === NOTE_SAVE) {
        dispatchReceiveNoteMessage(message)
    } else if (message.action === NOTE_DELETE) {
        dispatchDeleteNoteMessage(message)
    } else if (message.action === ROOM_STATE) {
        if (!(message.state === QUIZ && !message.teacher_present)) {
            dispatchReceiveRoomStateMessage(message)

            // check if room type is group study , if so fetch current state of group study
            roomStateHandler(message)
        }
    } else if (message.action === ACTIVE_SPEAKER_DATA) {
        dispatchReceiveTtmMessage(message)
    } else if (message.action === GESTURE) {
        const { gesture, gesture_type, from_user, gesture_pk } = message.data

        /* event should be emitted only when browser tab is in focus */
        !document.hidden &&
            emitGestureMsg({
                gestureKey: gesture,
                gestureType: gesture_type,
                autoHide:
                    gesturesData.find(item => item.gestureKey === gesture)
                        .autoRevert !== false,
                sender: from_user,
                gesturePK: gesture_pk,
            })
        !isDesktop && dispatchReceiveChatMessage(message)
    } else if (message.action === REACTION) {
        /* event should be emitted only when browser tab is in focus */
        const { id, type } = message.data

        if (type === EMOJI) {
            !document.hidden &&
                emitShowReactionMsg({
                    reactionId: id,
                    sender: message.sender,
                })
        } else if (type === GIPHY) {
            !document.hidden &&
                emitShowGiphyMsg({
                    giphyId: id,
                    sender: message.sender,
                })
        }
    } else if (message.action === REVERT_GESTURE) {
        const { gesture_pk } = message.data

        emitRevertGestureMsg({
            gesturePK: gesture_pk,
        })
    } else if ([MUTE].includes(message.action)) {
        dispatchReceiveAudioMessage(message)
    } else if (message.action === VIBE) {
        emitVibe(message)
    } else if (message.action === TOMATO) {
        emitGreetingTomato(message)
    } else if (message.action === CUPCAKE) {
        emitGreetingCupcake(message)
    } else if (message.action === ELSEWHERE) {
        if (typeof message.key === 'object') {
            dispatchPopulateDummyNotesMessages(message.key)
            dispatchSetSenderIdForDummyNotes(message.user_id)
        } else {
            emitElsewhereMessage(message)
            const { key, sender } = message
            const isElsewhere = key === elsewhereKeys.USER_IS_AWAY
            dispatchUpdateParticipantInfo(sender, 'isElsewhere', isElsewhere)
        }
    } else {
        dispatchReceiveAllMessage(message)
    }
}

function WebChatSocket() {
    const socket = useSelector(_socket)
    const interactionMood = useSelector(_isInteractionMode)
    const { room, roomName, userToken } = useVideoContext()
    const sw = useRef(null)
    const token = `token=${userToken}`
    const mood = `mood=${interactionMood}`
    const sessionId = `session_id=${room.localParticipant.sid}`
    const query = `${token}&${sessionId}&${mood}`
    const { MEETS_WS_URL } = getEnvUrls()
    const url = `${MEETS_WS_URL}/ws/websockets/${roomName}/?${query}`

    useHeartbeat()

    useEffect(() => {
        const isOpen = () => {
            dispatchSetSocket(sw.current)
        }

        const isError = event => {
            logger.error({
                msg: `Socket Error`,
                event,
            })
        }

        const isClose = event => {
            logger.info({ msg: `Socket closed`, event })
        }

        const initialiseSocketListeners = () => {
            sw.current.onopen = isOpen
            sw.current.onmessage = isMessage
            sw.current.onclose = isClose
            sw.current.onerror = isError
        }

        if (!sw.current) {
            sw.current = socket || new ReconnectingWebSocket(url)
            initialiseSocketListeners()
        }

        return () => {
            sw.current.close()
            sw.current = null
            dispatchSetSocket(null)
        }
    }, [])

    console.count('WebChatSocket component')

    return <span />
}

export default WebChatSocket
